
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Chip,
  Stack,
  Divider,
  Box,
  IconButton,
  Paper,
  MenuItem,
} from "@mui/material";

import HotelIcon from "@mui/icons-material/Hotel";
import PaymentsIcon from "@mui/icons-material/Payments";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BuildIcon from "@mui/icons-material/Build";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import CleaningServicesOutlinedIcon from "@mui/icons-material/CleaningServicesOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";

const actionTitles = {
  details: "Room Details",
  schedule: "Room Schedule",
  maintenance: "Room Maintenance",
  cleaning: "Room Cleaning",
};

const emptyRoom = {
  id: null,
  roomType: "",
  dailyPrice: "",
  monthlyPrice: "",
  description: "",
  fromDate: "",
  toDate: "",
  price2To3Hours: "",
  price4To6Hours: "",
  price6To12Hours: "",
  maximumRoomOccupancy: "",
  roomCount: "",
  roomNumber: "",
  availableRoomnumber: null,
  inventoryTrackingType: "",
  availableBedCount: "",
  availableRoomCount: "",
  availabilityCount: "",
  microstayPricingEnabled: "N",
};

const initialTaskDetails = {
  scheduledDate: "",
  scheduledTime: "",
  technician: "",
  priority: "MEDIUM",
  status: "SCHEDULED",
  estimatedDuration: "30 minutes",
  notes: "",
};

const cardSx = {
  p: 1.5,
  borderRadius: 1.5,
  borderColor: "#e5e7eb",
  boxShadow: "none",
  bgcolor: "#fff",
};

const normalizeDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.substring(0, 10);
  return "";
};

const isMicrostayEnabled = (value) =>
  ["Y", "YES", "TRUE"].includes(
    String(value ?? "").trim().toUpperCase()
  );

const normalizeRoom = (room = {}) => ({
  ...emptyRoom,
  ...room,
  dailyPrice: room.dailyPrice ?? "",
  monthlyPrice: room.monthlyPrice ?? "",
  fromDate: normalizeDate(room.fromDate),
  toDate: normalizeDate(room.toDate),
  roomCount: room.roomCount ?? "",
  roomNumber: room.roomNumber ?? "",
  maximumRoomOccupancy: room.maximumRoomOccupancy ?? "",
  inventoryTrackingType: room.inventoryTrackingType ?? "",
  availableRoomCount: room.availableRoomCount ?? "",
  availableBedCount: room.availableBedCount ?? "",
  availabilityCount: room.availabilityCount ?? "",
  microstayPricingEnabled: String(
    room.microstayPricingEnabled ?? "N"
  )
    .trim()
    .toUpperCase(),
  price2To3Hours: room.price2To3Hours ?? "",
  price4To6Hours: room.price4To6Hours ?? "",
  price6To12Hours: room.price6To12Hours ?? "",
});

function SectionHeading({ icon, title, subtitle }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ mb: 1.5 }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f3f4f6",
          color: "#555",
          flexShrink: 0,
          "& svg": {
            fontSize: 17,
          },
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          color="#30343b"
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function ReadOnlyField({ label, value }) {
  const displayValue =
    value !== null && value !== undefined && value !== ""
      ? String(value)
      : "—";

  return (
    <TextField
      fullWidth
      label={label}
      value={displayValue}
      size="small"
      InputProps={{ readOnly: true }}
      sx={{
        "& .MuiInputBase-root": {
          minHeight: 40,
          bgcolor: "#f9fafb",
        },
      }}
    />
  );
}

function EditableField({
  label,
  value,
  onChange,
  type = "text",
  ...props
}) {
  return (
    <TextField
      fullWidth
      label={label}
      type={type}
      value={value ?? ""}
      onChange={onChange}
      size="small"
      InputLabelProps={
        type === "date" || type === "time"
          ? { shrink: true }
          : undefined
      }
      inputProps={
        type === "number"
          ? { min: 0, step: "any" }
          : undefined
      }
      sx={{
        "& .MuiInputBase-root": {
          minHeight: 40,
        },
      }}
      {...props}
    />
  );
}

export default function RoomActionDialog({
  open,
  onClose,
  action = "details",
  room,
  roomDetailsList = [],
  selectedRoomIndex = 0,
  onSave,
  loading = false,
  detailsError = "",
  scheduleDetails = null,
  scheduleError = "",
}) {
  const [roomDetails, setRoomDetails] = useState(emptyRoom);
  const [roomNumbers, setRoomNumbers] = useState([]);
  const [taskDetails, setTaskDetails] = useState(initialTaskDetails);

  useEffect(() => {
    if (!open) return;

    const currentRoom =
      (Array.isArray(roomDetailsList)
        ? roomDetailsList[selectedRoomIndex]
        : null) ??
      room ??
      {};

    const normalizedRoom = normalizeRoom(currentRoom);

    setRoomDetails(normalizedRoom);

    setRoomNumbers(
      String(normalizedRoom.roomNumber ?? "")
        .split(",")
        .map((number) => number.trim())
        .filter(Boolean)
    );

    setTaskDetails({ ...initialTaskDetails });
  }, [open, room, roomDetailsList, selectedRoomIndex, action]);

  const updateField = (field) => (event) => {
    const value = event.target.value;

    setRoomDetails((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const updateTaskField = (field) => (event) => {
    const value = event.target.value;

    setTaskDetails((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const microstayEnabled = isMicrostayEnabled(
    roomDetails.microstayPricingEnabled
  );

  const handleSave = () => {
    const updatedRoom = {
      ...roomDetails,
      dailyPrice:
        roomDetails.dailyPrice === ""
          ? null
          : Number(roomDetails.dailyPrice),
      monthlyPrice:
        roomDetails.monthlyPrice === ""
          ? null
          : Number(roomDetails.monthlyPrice),
      price2To3Hours:
        roomDetails.price2To3Hours === ""
          ? null
          : Number(roomDetails.price2To3Hours),
      price4To6Hours:
        roomDetails.price4To6Hours === ""
          ? null
          : Number(roomDetails.price4To6Hours),
      price6To12Hours:
        roomDetails.price6To12Hours === ""
          ? null
          : Number(roomDetails.price6To12Hours),
      fromDate: roomDetails.fromDate || null,
      toDate: roomDetails.toDate || null,
    };

    const updatedRoomDetailsList = Array.isArray(roomDetailsList)
      ? roomDetailsList.map((item, index) =>
          index === selectedRoomIndex
            ? { ...item, ...updatedRoom }
            : item
        )
      : [updatedRoom];

    onSave?.({
      action,
      room: room ?? updatedRoom,
      roomDetails: updatedRoom,
      roomDetailsList: updatedRoomDetailsList,
    });
  };

  const handleTaskConfirm = () => {
    onSave?.({
      action,
      room,
      roomDetails,
      taskDetails: { ...taskDetails },
    });
  };

  const title = actionTitles[action] || "Room Details";

  const showRoomDetails = action === "details";
  const showSchedule = action === "schedule";
  const showMaintenance = action === "maintenance";
  const showCleaning = action === "cleaning";

  const renderTaskForm = (taskType) => {
    const isMaintenance = taskType === "maintenance";
    const accent = isMaintenance ? "#b45309" : "#047857";
    const softBg = isMaintenance ? "#fff7ed" : "#ecfdf5";
    const borderColor = isMaintenance ? "#fed7aa" : "#a7f3d0";

    const currentRoomNumber =
      room?.number ??
      room?.roomNumber ??
      roomDetails.roomNumber ??
      "—";

    return (
      <Stack spacing={1.5}>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 1.5,
            color: accent,
            bgcolor: softBg,
            border: `1px solid ${borderColor}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#fff",
                flexShrink: 0,
              }}
            >
              {isMaintenance ? (
                <BuildCircleOutlinedIcon sx={{ fontSize: 22 }} />
              ) : (
                <CleaningServicesOutlinedIcon sx={{ fontSize: 22 }} />
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {isMaintenance
                  ? "Maintenance Request"
                  : "Cleaning Task"}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {isMaintenance
                  ? "Record the issue and assign a technician."
                  : "Schedule room cleaning and assign staff."}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                ROOM TYPE
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {roomDetails.roomType ||
                  room?.roomType ||
                  room?.type ||
                  "—"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                ROOM NUMBER
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {currentRoomNumber}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={cardSx}>
          <SectionHeading
            icon={<ScheduleOutlinedIcon />}
            title="Schedule"
            subtitle="Choose when the task should take place"
          />

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <EditableField
                label="Scheduled Date"
                type="date"
                value={taskDetails.scheduledDate}
                onChange={updateTaskField("scheduledDate")}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <EditableField
                label="Scheduled Time"
                type="time"
                value={taskDetails.scheduledTime}
                onChange={updateTaskField("scheduledTime")}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <EditableField
                label="Estimated Duration"
                value={taskDetails.estimatedDuration}
                onChange={updateTaskField("estimatedDuration")}
                placeholder="e.g. 30 minutes"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <EditableField
                label={
                  isMaintenance
                    ? "Technician Name"
                    : "Cleaning Staff"
                }
                value={taskDetails.technician}
                onChange={updateTaskField("technician")}
                placeholder="Enter staff name"
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={cardSx}>
          <SectionHeading
            icon={<FlagOutlinedIcon />}
            title="Task Management"
            subtitle="Set the priority and current status"
          />

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                size="small"
                label="Priority"
                value={taskDetails.priority}
                onChange={updateTaskField("priority")}
              >
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                {isMaintenance && (
                  <MenuItem value="URGENT">Urgent</MenuItem>
                )}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                size="small"
                label="Status"
                value={taskDetails.status}
                onChange={updateTaskField("status")}
              >
                <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  size="small"
                  label={`Priority: ${taskDetails.priority}`}
                  color={
                    taskDetails.priority === "URGENT" ||
                    taskDetails.priority === "HIGH"
                      ? "error"
                      : taskDetails.priority === "MEDIUM"
                        ? "warning"
                        : "default"
                  }
                />

                <Chip
                  size="small"
                  label={taskDetails.status.replace("_", " ")}
                  color={
                    taskDetails.status === "COMPLETED"
                      ? "success"
                      : taskDetails.status === "IN_PROGRESS"
                        ? "info"
                        : "default"
                  }
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={cardSx}>
          <SectionHeading
            icon={<NotesOutlinedIcon />}
            title={
              isMaintenance
                ? "Issue & Repair Notes"
                : "Cleaning Instructions"
            }
            subtitle={
              isMaintenance
                ? "Describe the issue or required repair"
                : "Add special cleaning requirements"
            }
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            placeholder={
              isMaintenance
                ? "e.g. Bathroom tap leaking, replace bulb, inspect AC..."
                : "e.g. Clean bathroom, change bedsheets, sanitize surfaces..."
            }
            value={taskDetails.notes}
            onChange={updateTaskField("notes")}
          />
        </Paper>
      </Stack>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 600,
          borderRadius: 2,
          maxHeight: "88vh",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          position: "relative",
          px: 2.5,
          py: 1.75,
          pr: 7,
          bgcolor: "#fff",
          color: "#202124",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ fontSize: "16px", lineHeight: 1.5 }}
        >
          {title}
        </Typography>

        {/* CLOSE BUTTON */}
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{
            position: "absolute",
            top: 8,
            right: 12,
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: "rgba(0,0,0,0.04)",
            border: "1px solid rgba(0,0,0,0.08)",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.08)",
            },
            fontSize: "20px",
            fontWeight: 500,
            color: "#333",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ×
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: "#fff",
          borderTop: 0,
          borderBottom: 0,
          overflowY: "auto",
          "&:first-of-type": {
            paddingTop: 2,
          },
        }}
      >
        {detailsError && (
          <Typography
            color="error"
            variant="body2"
            sx={{ mb: 1.5 }}
          >
            {detailsError}
          </Typography>
        )}

        {showSchedule && scheduleError && (
          <Typography
            color="error"
            variant="body2"
            sx={{ mb: 1.5 }}
          >
            {scheduleError}
          </Typography>
        )}

        {showRoomDetails && (
          <Stack spacing={1.5}>
            <Paper variant="outlined" sx={cardSx}>
              <SectionHeading
                icon={<HotelIcon />}
                title="Room Information"
                subtitle="Room configuration and availability"
              />

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <ReadOnlyField
                    label="Room Type"
                    value={roomDetails.roomType}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ReadOnlyField
                    label="Total Rooms"
                    value={roomDetails.roomCount}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ReadOnlyField
                    label="Maximum Room Occupancy"
                    value={roomDetails.maximumRoomOccupancy}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ReadOnlyField
                    label="Available Rooms"
                    value={roomDetails.availableRoomCount}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={cardSx}>
              <SectionHeading
                icon={<PaymentsIcon />}
                title="Pricing"
                subtitle="Standard room rates"
              />

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <EditableField
                    label="Daily Price"
                    type="number"
                    value={roomDetails.dailyPrice}
                    onChange={updateField("dailyPrice")}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <EditableField
                    label="Monthly Price"
                    type="number"
                    value={roomDetails.monthlyPrice}
                    onChange={updateField("monthlyPrice")}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={cardSx}>
              <SectionHeading
                icon={<CalendarMonthIcon />}
                title="Price Validity"
                subtitle="Validity period for room rates"
              />

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <EditableField
                    label="From Date"
                    type="date"
                    value={roomDetails.fromDate}
                    onChange={updateField("fromDate")}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <EditableField
                    label="To Date"
                    type="date"
                    value={roomDetails.toDate}
                    onChange={updateField("toDate")}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={cardSx}>
              <SectionHeading
                icon={<MeetingRoomIcon />}
                title="Room Numbers"
                subtitle="Assigned room numbers"
              />

              {roomNumbers.length > 0 ? (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={0.75}
                >
                  {roomNumbers.map((number, index) => (
                    <Chip
                      key={`${number}-${index}`}
                      label={number}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  No room numbers assigned.
                </Typography>
              )}
            </Paper>

            <Paper variant="outlined" sx={cardSx}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 1.5 }}
              >
                <SectionHeading
                  icon={<AccessTimeIcon />}
                  title="Microstay Pricing"
                  subtitle="Short-duration room rates"
                />

                <Chip
                  label={microstayEnabled ? "Enabled" : "Disabled"}
                  color={microstayEnabled ? "success" : "default"}
                  size="small"
                />
              </Stack>

              {microstayEnabled ? (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <EditableField
                      label="2–3 Hours Price"
                      type="number"
                      value={roomDetails.price2To3Hours}
                      onChange={updateField("price2To3Hours")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <EditableField
                      label="4–6 Hours Price"
                      type="number"
                      value={roomDetails.price4To6Hours}
                      onChange={updateField("price4To6Hours")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <EditableField
                      label="6–12 Hours Price"
                      type="number"
                      value={roomDetails.price6To12Hours}
                      onChange={updateField("price6To12Hours")}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Microstay pricing is disabled for this room type.
                </Typography>
              )}
            </Paper>
          </Stack>
        )}

        {showSchedule && (
          <Stack spacing={1.5}>
            <Paper variant="outlined" sx={cardSx}>
              <SectionHeading
                icon={<CalendarMonthIcon />}
                title="Room Schedule"
                subtitle="Room pricing validity period"
              />

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                Room: {roomDetails.roomType || "—"}
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <ReadOnlyField
                    label="From Date"
                    value={roomDetails.fromDate}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ReadOnlyField
                    label="To Date"
                    value={roomDetails.toDate}
                  />
                </Grid>
              </Grid>
            </Paper>

            {scheduleDetails && (
              <Paper variant="outlined" sx={cardSx}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 1 }}
                >
                  Schedule Information
                </Typography>

                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    fontFamily: "inherit",
                    fontSize: 13,
                  }}
                >
                  {typeof scheduleDetails === "string"
                    ? scheduleDetails
                    : JSON.stringify(scheduleDetails, null, 2)}
                </Box>
              </Paper>
            )}
          </Stack>
        )}

        {showMaintenance && renderTaskForm("maintenance")}

        {showCleaning && renderTaskForm("cleaning")}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          gap: 1,
          bgcolor: "#fff",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          size="small"
          disabled={loading}
          sx={{
            textTransform: "none",
            minWidth: 85,
            borderColor: "#d1d5db",
          }}
        >
          Cancel
        </Button>

        {showRoomDetails && (
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            disabled={loading}
            sx={{
              textTransform: "none",
              minWidth: 110,
              boxShadow: "none",
            }}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        )}

        {(showSchedule || showMaintenance || showCleaning) && (
          <Button
            onClick={handleTaskConfirm}
            variant="contained"
            size="small"
            disabled={loading}
            startIcon={
              showMaintenance ? (
                <BuildIcon />
              ) : showCleaning ? (
                <CleaningServicesOutlinedIcon />
              ) : (
                <CalendarMonthIcon />
              )
            }
            sx={{
              textTransform: "none",
              minWidth: 110,
              boxShadow: "none",
            }}
          >
            {loading
              ? "Processing..."
              : showMaintenance
                ? "Save Maintenance"
                : showCleaning
                  ? "Schedule Cleaning"
                  : "Confirm"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
